/**
 * V4-ZERNIO-COMMENTS — Service frontend pour l'inbox de commentaires.
 *
 * Endpoints :
 *   GET  /api/v1/inbox/comments?space_id=&status=&network=
 *   GET  /api/v1/inbox/comments/count?space_id=&status=unreplied
 *   POST /api/v1/inbox/comments/{comment_id}/reply
 *   POST /api/v1/inbox/comments/{comment_id}/hide
 *   POST /api/v1/inbox/comments/{comment_id}/like
 */
import { apiClient } from './apiClient';
import { Logger } from '../utils/logger';

export interface InboxComment {
  comment_id: string;
  zernio_post_id?: string;
  post_id?: string;
  space_id: string;
  user_id?: string;
  channel_id?: string;
  channel_network?: string;
  platform?: string;
  platform_comment_id?: string;
  parent_comment_id?: string;
  author_name?: string;
  author_id?: string;
  author_avatar_url?: string;
  content: string;
  created_at?: string;
  fetched_at?: string;
  replied: boolean;
  replied_at?: string;
  reply_content?: string;
  hidden: boolean;
  liked: boolean;
}

export interface CommentsListResponse {
  items: InboxComment[];
  total: number;
}

export interface CommentsCountResponse {
  count: number;
}

export type CommentStatusFilter = 'all' | 'unreplied' | 'replied' | 'hidden';

export const zernioCommentsService = {
  list: async (
    spaceId: string,
    options: { status?: CommentStatusFilter; network?: string; limit?: number; offset?: number } = {},
  ): Promise<CommentsListResponse> => {
    Logger.debug('[ZernioComments] List', { spaceId, ...options });
    const params: Record<string, string | number> = { space_id: spaceId };
    if (options.status) params.status = options.status;
    if (options.network) params.network = options.network;
    if (options.limit !== undefined) params.limit = options.limit;
    if (options.offset !== undefined) params.offset = options.offset;
    const response = await apiClient.get<CommentsListResponse>('/inbox/comments', { params });
    return response.data;
  },

  countUnreplied: async (spaceId: string): Promise<number> => {
    // V4-ZERNIO-INBOX-BADGE — route canonique /inbox/comments/* (les routes /comments/*
    // ne marchaient que via un alias backend fragile → 404 si l'alias n'est pas déployé).
    const response = await apiClient.get<CommentsCountResponse>('/inbox/comments/count', {
      params: { space_id: spaceId, status: 'unreplied' },
    });
    return response.data.count ?? 0;
  },

  reply: async (commentId: string, content: string): Promise<{ ok: boolean }> => {
    Logger.info('[ZernioComments] Reply', { commentId, len: content.length });
    const response = await apiClient.post<{ ok: boolean }>(
      `/inbox/comments/${commentId}/reply`,
      { content },
    );
    return response.data;
  },

  hide: async (commentId: string): Promise<{ ok: boolean }> => {
    Logger.info('[ZernioComments] Hide', { commentId });
    const response = await apiClient.post<{ ok: boolean }>(`/inbox/comments/${commentId}/hide`);
    return response.data;
  },

  like: async (commentId: string): Promise<{ ok: boolean }> => {
    Logger.info('[ZernioComments] Like', { commentId });
    const response = await apiClient.post<{ ok: boolean }>(`/inbox/comments/${commentId}/like`);
    return response.data;
  },
};
